import axios from "axios";

interface ApiError {
  message: string;
  status: number;
  code?: string;
}

function extractErrorFromBody(
  body: unknown,
): { message: string; code?: string } | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const errorPayload = record.error as
    Record<string, unknown> | string | undefined;

  if (typeof record.message === "string" && record.message.trim()) {
    return {
      message: record.message,
      code:
        (record.code as string | undefined) ||
        (typeof errorPayload === "object"
          ? (errorPayload?.code as string | undefined)
          : undefined),
    };
  }
  if (typeof errorPayload === "string" && errorPayload.trim()) {
    return {
      message: errorPayload,
      code: record.code as string | undefined,
    };
  }
  if (errorPayload && typeof errorPayload === "object") {
    return {
      message:
        (errorPayload.message as string) || "An unexpected error occurred",
      code:
        (errorPayload.code as string) || (record.code as string | undefined),
    };
  }
  return null;
}

export const handleApiError = async (error: unknown): Promise<ApiError> => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status || 0;
    let body = error.response?.data as unknown;

    // Blob/ArrayBuffer responses (downloads) carry the error payload in the
    // body — read and parse it so the real server message is surfaced.
    if (body instanceof Blob && body.size > 0) {
      try {
        body = JSON.parse(await body.text());
      } catch {
        body = undefined;
      }
    } else if (body instanceof ArrayBuffer && body.byteLength > 0) {
      try {
        body = JSON.parse(new TextDecoder().decode(body));
      } catch {
        body = undefined;
      }
    }

    const extracted = extractErrorFromBody(body);
    if (extracted) {
      return { message: extracted.message, status, code: extracted.code };
    }

    return { message: error.message || "An unexpected error occurred", status };
  }

  if (error instanceof Error) {
    return { message: error.message, status: 0 };
  }

  return { message: "An unexpected error occurred", status: 0 };
};
